import React from 'react';
import { Box, Stack, Container } from '@mui/material';
import SEOHead from './SEO/SEOHead';
import { Typography, Card, CardContent, CardMedia, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import SectionTitle from './shared/SectionTitle';

const PageContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
}));

const HeroSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #1a2e4a 0%, #2a4a6e 100%)',
    color: '#ffffff',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23ffffff' fill-opacity='0.02'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px',
    },
}));

const StyledImage = styled('img')(({ topImage }: { topImage?: boolean }) => ({
    width: 'auto',
    height: 'auto',
    maxWidth: topImage ? '800px' : '500px'
}));

const ClothSection = ({ title, description, weights, image, link }: {
    title: string;
    description?: string;
    weights: string;
    image?: string;
    link?: string;
}) => (
    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="flex-start">
        <Box flex={1}>
            <Typography variant="h5" gutterBottom><strong>{title}</strong></Typography>
            {description && <Typography sx={{ mb: 2 }}>{description}</Typography>}
            <Typography>{weights}</Typography>
            {link && (
                <Box sx={{ mt: 2 }}>
                    <Link href={link} target="_blank" rel="noopener">Product Details</Link>
                </Box>
            )}
        </Box>
        {image && (
            <Box flex={1}>
                <Card sx={{
                    width: 'fit-content',
                    backgroundColor: 'transparent',
                    boxShadow: 'none'
                }}>
                    <CardContent>
                        <StyledImage src={`/images/cloth/${image}`} alt={title} />
                    </CardContent>
                </Card>
            </Box>
        )}
    </Stack>
);

const SailclothPage = () => {
    return (
        <PageContainer>
            <SEOHead page="sailCloth" />
            
            {/* Navy Hero Section */}
            <HeroSection>
                <Box sx={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            fontWeight: 500,
                            mb: 2,
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            color: '#ffffff',
                        }}
                    >
                        Sail Cloth
                    </Typography>
                    <Typography 
                        sx={{ 
                            fontSize: '1.15rem',
                            fontWeight: 300,
                            opacity: 0.9,
                            maxWidth: '600px',
                            margin: '0 auto',
                            color: '#ffffff',
                        }}
                    >
                        Premium Fabrics from Industry-Leading Manufacturers
                    </Typography>
                </Box>
            </HeroSection>

            <Container maxWidth="lg" sx={{ p: 3 }}>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <StyledImage
                        src="/images/cloth/warp-and-fill.jpg"
                        alt="Warp and Fill"
                        topImage
                    />
                </Box>

                <SectionTitle centered sx={{ mb: 6 }}>
                    Dacron
                </SectionTitle>

                <Stack spacing={6}>
                    <ClothSection
                        title="Challenge Performance Cruise"
                        weights="6.18 7.18 8.18 9.18 10.18"
                    />

                    <ClothSection
                        title="Challenge High Mass Fiber Dacron"
                        description="Tight weave construction of tough yarns for sails on boats that see a lot of use. The large warp yarns will resist abrasion, tear, UV and general use better than smaller fibers."
                        weights="7.38 7.88 8.88 9.88 10.88"
                    />

                    <ClothSection
                        title="Challenge High Modulus Dacron"
                        description="High performance racing and cruising fabrics. Available in low and high aspect constructions. The 6.03 is available in a wide range of colors."
                        weights="Low Aspect
4.93 5.53 6.03 6.53 7.03 8.03 9.03 10.53 11.93

High Aspect
5.93 6.63 7.3 8.3 9.3 10.3"
                    />

                    <ClothSection
                        title="Challenge Warp Drive Dacron"
                        description="Challenge's patented Warp Drive for dacron tri-radial construction. Great shape holding characteristics."
                        weights="Warp Drive
6.11 7.11 8.11 10.11"
                    />

                    <SectionTitle centered sx={{ my: 4 }}>
                        Laminate
                    </SectionTitle>

                    <ClothSection
                        title="Dimension Polyant Laminates"
                        description="The PX (Polyester), PE (Pentex), X-Tech (Aramid), Carbon Sport and GPL (Technora/Carbon) fabrics are warp oriented for radial constructions. Film, scrim, X-Ply and film make up this durable fabric. Available in LiteSkin"
                        weights=""
                        image="dp-pxb10.jpg"
                    />

                    <ClothSection
                        title=""
                        weights=""
                        image="dp-cs10.jpg"
                    />

                    <SectionTitle centered sx={{ my: 4 }}>
                        Downwind Sails
                    </SectionTitle>

                    <ClothSection
                        title="Challenge Fibermax Nylon"
                        description="Available in .75oz (Fibermax 44), 1.5oz (Fibermax 64) and 2.2oz (Fibermax 94) in a selection of colors."
                        weights=""
                        image="challenge-spin-colors.jpg"
                    />

                    <ClothSection
                        title="Dimension Polyant Code Zero Laminate"
                        description="High performance laminate for code zero and light air genoas."
                        weights=""
                        link="https://www.dimension-polyant.com/product/code-zero-optic-2/"
                    />
                </Stack>
            </Container>
        </PageContainer>
    );
};

export default SailclothPage;